// Settings → Ledger Mapping (V-12, ADR-0035): the business's accounts, suppliers and customers and Dimension values in
// three lists, each with its name in Tally or "Not mapped". Attaching a Tally masters export shows what LAMP proposes, as
// a preview: nothing is saved until Confirm, what is wrong is unticked, and what could not be matched is listed to map
// by hand or leave. One is mapped by hand with a search of Tally's names from the last export confirmed. Only a person
// whose Roles let them map sees the buttons. Tally's names are untrusted data: shown as text, never as markup.
import { useCallback, useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { useWords } from "../../i18n/words.js";
import { changeProblem } from "../ListRow.js";
import type { BooksName, LedgerMappingApi, LedgerMappingLists, Mapping, MappingRow, Proposal } from "./api.js";

const keyOf = (m: { kind: string; subject: string }) => `${m.kind}:${m.subject}`;
/**
 * Whether Tally's name `m` is of the kind `row` maps to: a cost centre, a party ledger (as the Application API marks
 * one: a supplier or customer maps only to one), or another ledger.
 */
const fits = (row: MappingRow, m: BooksName) =>
  row.kind === "dimension_value"
    ? m.kind === "cost_centre"
    : m.kind === "ledger" && m.party === (row.kind === "counterparty");

function MapRow({
  row,
  may,
  busy,
  api,
  onMap,
}: {
  row: MappingRow;
  may: boolean;
  busy: boolean;
  api: LedgerMappingApi;
  onMap: (name: string) => Promise<boolean>;
}) {
  const w = useWords();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(row.name ?? "");
  const [found, setFound] = useState<readonly BooksName[]>([]);
  const search = (words: string) => {
    setName(words);
    if (words.trim() === "") {
      setFound([]);
      return;
    }
    void api.names(words.trim()).then(
      (names) => setFound(names.masters.filter((m) => fits(row, m))),
      () => setFound([]),
    );
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (await onMap(name.trim())) setEditing(false);
  };
  const headingId = `mapping-${row.kind}-${row.subject}`;
  return (
    <li className="card" aria-labelledby={headingId}>
      {/* The business's own names and Tally's are data, not words: no language translates them. */}
      <h3 id={headingId} translate="no">
        {row.lamp}
      </h3>
      <p className="muted" translate="no">
        {row.detail}
      </p>
      <p>
        {row.name === null ? (
          w.mappingNotMapped
        ) : (
          <>
            {w.mappingInTally} <span translate="no">{row.name}</span>
          </>
        )}
      </p>
      {may && !editing ? (
        <button type="button" className="btn" aria-label={w.mappingMap(row.lamp)} onClick={() => setEditing(true)}>
          {row.name === null ? w.mappingMapButton : w.mappingChangeButton}
        </button>
      ) : null}
      {editing ? (
        <form aria-label={w.mappingMap(row.lamp)} onSubmit={(event) => void submit(event)}>
          <label className="field">
            {w.mappingTallyNameField}{" "}
            <input
              aria-label={w.mappingSearch(row.lamp)}
              value={name}
              maxLength={200}
              onChange={(event) => search(event.target.value)}
            />
          </label>
          {found.length > 0 ? (
            <ul aria-label={w.mappingFound}>
              {found.map((m) => (
                <li key={`${m.kind}:${m.name}`}>
                  <button type="button" className="btn" translate="no" onClick={() => setName(m.name)}>
                    {m.name}
                  </button>{" "}
                  {m.group ? (
                    <span className="muted" translate="no">
                      {m.group}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
          <button type="submit" className="btn btn-go" disabled={busy || name.trim() === ""}>
            {w.listSave}
          </button>
          <button type="button" className="btn" onClick={() => setEditing(false)}>
            {w.listCancel}
          </button>
        </form>
      ) : null}
    </li>
  );
}

function ProposalPreview({
  proposal,
  busy,
  onConfirm,
}: {
  proposal: Proposal;
  busy: boolean;
  onConfirm: (mappings: readonly Mapping[], accounts: readonly string[], values: readonly string[]) => void;
}) {
  const w = useWords();
  const [kept, setKept] = useState<ReadonlySet<string>>(() => new Set(proposal.proposed.map(keyOf)));
  const [accounts, setAccounts] = useState<ReadonlySet<string>>(new Set());
  const [values, setValues] = useState<ReadonlySet<string>>(new Set());
  const toggle = (set: ReadonlySet<string>, key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  };
  const offerValues = proposal.new_dimension_values;
  const nothing =
    proposal.proposed.length === 0 &&
    proposal.new_expense_accounts.length === 0 &&
    (offerValues?.names.length ?? 0) === 0;
  return (
    <section className="section" aria-labelledby="mapping-proposal">
      <h2 id="mapping-proposal">{w.mappingProposalHeading}</h2>
      <p className="muted">{nothing ? w.mappingNothingNew : w.mappingProposalNote}</p>
      {proposal.proposed.length > 0 ? (
        <ul className="queue">
          {proposal.proposed.map((p) => (
            <li key={keyOf(p)}>
              <label>
                <input
                  type="checkbox"
                  checked={kept.has(keyOf(p))}
                  onChange={() => setKept((k) => toggle(k, keyOf(p)))}
                />{" "}
                <span translate="no">{p.label ? `${p.lamp} (${p.label})` : p.lamp}</span> →{" "}
                <span translate="no">{p.name}</span> ({w.mappingBy(p.by)})
              </label>
            </li>
          ))}
        </ul>
      ) : null}
      {proposal.new_expense_accounts.length > 0 ? (
        <fieldset>
          <legend>{w.mappingOfferAccounts}</legend>
          {proposal.new_expense_accounts.map((a) => (
            <label key={a.name} className="field">
              <input
                type="checkbox"
                checked={accounts.has(a.name)}
                onChange={() => setAccounts((s) => toggle(s, a.name))}
              />{" "}
              <span translate="no">{a.name}</span>{" "}
              <span className="muted" translate="no">
                {a.group}
              </span>
            </label>
          ))}
        </fieldset>
      ) : null}
      {offerValues && offerValues.names.length > 0 ? (
        <fieldset>
          <legend>
            {w.mappingOfferValues} <span data-pack="">{offerValues.label}</span>
          </legend>
          {offerValues.names.map((name) => (
            <label key={name} className="field">
              <input type="checkbox" checked={values.has(name)} onChange={() => setValues((s) => toggle(s, name))} />{" "}
              <span translate="no">{name}</span>
            </label>
          ))}
        </fieldset>
      ) : null}
      {proposal.unmatched.length > 0 ? (
        <>
          <h3>{w.mappingUnmatchedHeading}</h3>
          <ul className="queue">
            {proposal.unmatched.map((u) => (
              <li key={keyOf(u)}>
                <strong translate="no">{u.label ? `${u.lamp} (${u.label})` : u.lamp}</strong>: {u.reason}
                {u.candidates ? (
                  <span className="muted">
                    {" "}
                    {w.mappingCandidates} <span translate="no">{u.candidates.join(", ")}</span>
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {nothing ? null : (
        <button
          type="button"
          className="btn btn-go"
          disabled={busy}
          onClick={() =>
            onConfirm(
              proposal.proposed
                .filter((p) => kept.has(keyOf(p)))
                .map(({ kind, subject, name }) => ({ kind, subject, name })),
              [...accounts],
              [...values],
            )
          }
        >
          {w.mappingConfirm}
        </button>
      )}
    </section>
  );
}

export function LedgerMapping({ api }: { api: LedgerMappingApi }) {
  const w = useWords();
  const [lists, setLists] = useState<LedgerMappingLists>();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>();
  const [problem, setProblem] = useState<string>();
  const [xml, setXml] = useState<string>();
  const [proposal, setProposal] = useState<Proposal>();
  /** Counts the proposals shown, so each starts afresh, even of an export the same size as the last. */
  const [proposals, setProposals] = useState(0);

  const load = useCallback(
    () =>
      api.list().then(
        (shown) => setLists(shown),
        (error: unknown) => setProblem(changeProblem(error, w)),
      ),
    [api, w],
  );
  useEffect(() => {
    void load();
  }, [load]);

  /** Runs a change, then shows the lists as they are after it; false when it was refused. */
  const change = async (run: () => Promise<string | undefined>) => {
    setBusy(true);
    setNotice(undefined);
    setProblem(undefined);
    try {
      const said = await run();
      await load();
      if (said) setNotice(said);
      return true;
    } catch (error) {
      setProblem(changeProblem(error, w));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const attach = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setXml(text);
    setProposal(undefined);
    await change(async () => {
      setProposal(await api.propose(text));
      setProposals((n) => n + 1);
      return undefined;
    });
  };

  if (!lists) return problem ? <p role="alert">{problem}</p> : <p role="status">{w.settingsLoading}</p>;
  const may = lists.may.map;
  const section = (id: string, title: string, rows: readonly MappingRow[]) => (
    <section key={id} className="section" aria-labelledby={`mapping-${id}`}>
      <h2 id={`mapping-${id}`}>{title}</h2>
      {rows.length === 0 ? <p className="muted">{w.mappingNothing}</p> : null}
      <ul className="queue">
        {rows.map((row) => (
          <MapRow
            key={keyOf(row)}
            row={row}
            may={may}
            busy={busy}
            api={api}
            onMap={(name) =>
              change(async () => {
                const mapped = await api.map({ kind: row.kind, subject: row.subject, name });
                return w.mappingSaved(row.lamp, mapped.name);
              })
            }
          />
        ))}
      </ul>
    </section>
  );
  return (
    <>
      <p className="muted">{w.mappingHelp}</p>
      {may ? null : <p className="muted">{w.listReadOnly}</p>}
      {notice ? <p role="status">{notice}</p> : null}
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      {may ? (
        <label className="field">
          {w.mappingAttach} <input type="file" accept=".xml,text/xml" onChange={(event) => void attach(event)} />
        </label>
      ) : null}
      {proposal && xml !== undefined ? (
        <ProposalPreview
          key={proposals}
          proposal={proposal}
          busy={busy}
          onConfirm={(mappings, accounts, values) =>
            void change(async () => {
              const done = await api.confirm({
                xml,
                mappings,
                add_expense_accounts: accounts,
                add_dimension_values: values,
              });
              setProposal(undefined);
              return w.mappingConfirmed(done.changes.length, done.added_accounts.length, done.added_values.length);
            })
          }
        />
      ) : null}
      {section("accounts", w.mappingAccounts, lists.accounts)}
      {section("parties", w.mappingParties, lists.parties)}
      {section("values", w.mappingValues, lists.values)}
    </>
  );
}
